// MarketResearchPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/strategyxs.png'


const MarketResearchPage = () => {
    window.scroll(0, 0)

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const researchStats = [
        { number: '88%', label: 'of successful businesses use market research to guide strategic marketing decisions' },
        { number: '3.5x', label: 'higher ROI on marketing spend with data-driven strategies informed by research' },
        { number: '70%', label: 'increase in campaign effectiveness with audience insight and market research' },
        { number: '40%', label: 'faster market entry with comprehensive competitive analysis and research' }
    ];

    const researchApproaches = [
        {
            icon: 'fas fa-users',
            title: 'Audience & Customer Insight Research',
            desc: 'Demographic and psychographic analysis, customer needs and preference profiling, and purchase motivators and barriers research to fine-tune messaging and targeting strategy.'
        },
        {
            icon: 'fas fa-chart-bar',
            title: 'Competitor & Market Landscape Analysis',
            desc: 'Comprehensive evaluation of market share, competitive product and channel strategies, strengths, weaknesses and gaps in the market for effective brand positioning.'
        },
        {
            icon: 'fas fa-barcode',
            title: 'Industry Trend & Consumer Behavior Research',
            desc: 'Tracking industry and consumer trends, emerging market opportunities, and identifying best-fit audience channels for future-proof marketing strategies.'
        },
        {
            icon: 'fas fa-comment-alt',
            title: 'Brand Sentiment & Reputation Analysis',
            desc: 'Analysis of public and social sentiment, brand awareness and associations, and perception gaps versus competitors to strengthen brand trust and loyalty.'
        },
        {
            icon: 'fas fa-poll',
            title: 'Surveys, Focus Groups & Qualitative Studies',
            desc: 'Combination of research methodologies including surveys, interviews, focus groups and digital analytics to build a 360° view of your audience and market context.'
        },
        {
            icon: 'fas fa-lightbulb',
            title: 'Insight-Driven Strategy Development',
            desc: 'Translating research findings into actionable strategic recommendations that drive growth, reduce risk, and optimize budget allocation for maximum impact.'
        }
    ];

    const benefits = [
        ['Clear Audience Understanding', 'Tailor messaging and targeting for better impact and higher conversion rates based on real data'],
        ['Informed Strategy Direction', 'Prioritise marketing initiatives based on evidence rather than assumptions or guesswork'],
        ['Competitive Market Advantage', 'Know where and how to outperform rivals with strategic positioning and market intelligence'],
        ['Smarter Budget Allocation', 'Invest in channels and tactics that deliver measurable results backed by research insights'],
        ['Reduced Strategic Risk', 'Make confident marketing decisions grounded in insights for sustainable business growth']
    ];

    const targetBusinesses = [
        'Brands launching new products or services to competitive markets',
        'Companies entering new market segments or geographic territories',
        'Marketing teams planning high-impact digital strategy and campaigns',
        'Organisations seeking deeper customer insight and competitive edge',
        'Startups validating business models and market fit before scaling',
        'Enterprises optimizing existing market positions and exploring expansion opportunities'
    ];

    const researchQuestions = [
        'Who are your ideal customers and what truly motivates their purchasing decisions?',
        'How does your brand perception compare to key competitors in the market?',
        'What emerging industry trends could impact your market position and growth?',
        'Where are the untapped opportunities in your industry and target market?',
        'What channels do your customers prefer for engagement and communication?'
    ];

    return (

        <div className="strategy sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className="sv-geometric-shape circle shape-1"></div>
            <div className="sv-geometric-shape square shape-2"></div>
            <div className="sv-geometric-shape circle shape-3"></div>
            <div className="sv-geometric-shape square shape-4"></div>
            <div className="sv-geometric-shape circle shape-5"></div>
            <div className="sv-geometric-shape square shape-6"></div>
            <div className="sv-geometric-shape circle shape-7"></div>
            <div className="sv-geometric-shape square shape-8"></div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Market Research</span> Services
                    </h1>
                    <p>Deep customer insights and competitive analysis to power smarter marketing strategy and sustainable business growth</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Understanding your audience, competitors and market dynamics is the foundation of any successful digital marketing strategy.
                                </p>
                                <p className="sv-description">
                                    Reachtix <strong>Market Research services</strong> provide your business with clear, actionable insight that helps you make confident decisions, reduce risk and unlock new opportunities in a rapidly evolving landscape. Our <strong>customer research</strong> and <strong>competitive analysis</strong> methodologies deliver evidence-based clarity.
                                </p>
                                <p className="sv-description">
                                    Rather than guessing what might work, you gain evidence-based clarity on what will work — ensuring your digital marketing efforts are targeted, relevant, and measurable through comprehensive <strong>market intelligence</strong> and research.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Market Research
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Research Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Market Research</span> Drives Business Success
                        </h2>
                        <Row className="sv-stats-grid">
                            {researchStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What Is Market Research Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is Market Research?</h2>
                                <p className="sv-section-description">
                                    <strong>Market research</strong> is the systematic process of gathering, analysing and interpreting data about your target customers, competitors, and industry trends. It reveals who your audience truly is, what drives their behaviour, where the opportunities lie, and how your brand compares in the competitive landscape through comprehensive <strong>customer insight</strong> analysis.
                                </p>
                                <p className="sv-section-description">
                                    In today's digital economy, <strong>market research</strong> underpins every high-impact decision — from refining proposition messaging to shaping channel strategy and forecasting future demand based on solid <strong>market intelligence</strong>.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Research Questions Our Services Answer:</h4>
                                    {researchQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Market Research Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Market Research Matters for Digital Success</h2>
                                <p className="sv-section-description">
                                    Without solid market insight, digital strategies can lack focus, overspend on underperforming channels, or fail to connect with the right audiences. <strong>Customer research</strong> provides the foundation for effective marketing.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Deeply Understand Your Audience</h5>
                                        <p>Deep insights into customer needs, motivations, and behaviors through comprehensive research</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Identify Market Trends Early</h5>
                                        <p>Stay ahead of market shifts and emerging opportunities with trend research</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-comments"></i>
                                        <h5>Shape Effective Messaging</h5>
                                        <p>Create positioning and content that truly resonates with your target market</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-shield-alt"></i>
                                        <h5>Reduce Strategic Risk</h5>
                                        <p>Make confident marketing decisions based on evidence, not assumptions</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Market Research Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Our Market Research Services at Reachtix</h2>
                                <p className="sv-section-description">
                                    We tailor our <strong>market research</strong> approach to your business goals, combining both quantitative and qualitative methods to uncover the insights that matter most for your <strong>competitive analysis</strong> and strategy.
                                </p>

                                <div className="sv-approaches-grid">
                                    {researchApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">The Business Benefits of Market Research</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Research Benefit</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([benefit, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{benefit}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    <strong>Market research</strong> ensures your digital strategy is neither guesswork nor assumption — it's grounded in real evidence and measurable intelligence that drives sustainable growth.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Who Should Invest in Market Research?</h2>
                                <p className="sv-section-description">
                                    <strong>Market research</strong> delivers maximum value for organizations seeking clarity, confidence, and competitive advantage in their marketing:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you are a startup, SME or enterprise, insight-led decisions drive stronger outcomes and sustainable growth through continuous <strong>customer research</strong> and analysis.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <h2>Ready to Make Insight-Led Marketing Decisions?</h2>
                        <p className="sv-cta-description">
                            <strong>Market research</strong> removes uncertainty and unlocks clarity. Let Reachtix's Market Research services equip you with the intelligence needed to grow confidently and strategically with data-backed <strong>customer insights</strong>.
                        </p>
                        <p className="sv-cta-description">
                            We blend analytical rigour with actionable strategy, translating research insights into strategic recommendations that drive measurable business growth.
                        </p>
                        <div className="sv-cta-buttons">
                            <Button
                                tag={Link}
                                to="/contact"
                                color='primary'
                            >
                                <i className="fas fa-search mr-2"></i>
                                Start Your Research Project
                            </Button>
                            <Button
                                tag={Link}
                                to="/contact"
                                color='light'
                            >
                                <i className="fas fa-comments mr-2"></i>
                                Get Research Consultation
                            </Button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default MarketResearchPage;